import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { EASE } from "./Reveal";
import BookingDialog from "./BookingDialog";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-between overflow-hidden px-6 md:px-12 pt-28 md:pt-32 pb-10"
    >
      <motion.div
        aria-hidden="true"
        style={{ y: bgY }}
        className="pointer-events-none absolute inset-0 flex items-center justify-center select-none reduce-motion-hidden"
      >
        <span
          className="font-serif italic text-[24vw] leading-none text-transparent whitespace-nowrap"
          style={{ WebkitTextStroke: "1px #E5E0D8" }}
        >
          growth
        </span>
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-[100rem] flex items-start justify-between text-[10px] md:text-xs uppercase tracking-[0.25em] text-ash">
        <MaskedLine delay={0.1}>Makkar Growth Partners</MaskedLine>
        <MaskedLine delay={0.2} className="text-right">17 yrs · CPG → B2B SaaS</MaskedLine>
      </div>

      <motion.h1
        style={{ y: textY }}
        className="relative z-10 mx-auto w-full max-w-[100rem] font-serif font-light tracking-tighter leading-[0.95] text-[15vw] md:text-[10.5vw]"
      >
        <MaskedLine delay={0.35}>Fractional CMO</MaskedLine>
        <MaskedLine delay={0.5}>expertise.</MaskedLine>
        <MaskedLine delay={0.65}>
          <em className="text-taupe">Full-time growth results.</em>
        </MaskedLine>
      </motion.h1>

      <div className="relative z-10 mx-auto w-full max-w-[100rem] flex flex-col md:flex-row md:items-end md:justify-between gap-8">
        <MaskedLine delay={0.9} className="max-w-md">
          <span className="text-sm md:text-base font-light text-ash leading-relaxed">
            Executive marketing leadership for companies that need a CMO-level
            brain — strategy, team, and budget ownership — without the full-time
            CMO overhead.
          </span>
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
          className="flex items-center gap-6"
        >
          <BookingDialog
            trigger={
              <button
                data-testid="hero-book-call-btn"
                className="group inline-flex items-center gap-3 bg-ink text-paper text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-taupe transition-colors duration-300"
              >
                Book a Free Strategy Call
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            }
          />
          <button
            data-testid="hero-scroll-btn"
            onClick={() => lenis?.scrollTo("#expertise", { offset: -72, duration: 1.4 })}
            aria-label="Scroll to content"
            className="hidden md:flex h-12 w-12 items-center justify-center border border-line hover:border-ink transition-colors duration-300"
          >
            <ArrowDown size={16} className="animate-bounce" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
